import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { displayDialog } from "store/actions/utilActions";
//#region types
import { CurrencyEnums } from "models/enums";
import { SalaryFormTypes } from "models/formTypes";
import { WintSuperkollAPIModelWintSalaryReport, WintUtilsWebApi } from "wint-api-client/dist/types";
import { User } from "models/propTypes/userProfilePropTypes";
import { WintSalaryReportListItem } from "models/propTypes/wintSalaryPropTypes";
import { Amount } from "models/utilTypes/currencyType";
import { TranslationKeys } from "translations";
import { DateConstants, IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import { Icon, SimpleTooltip, TableAsync, useFilterContext, useSearchContext } from "components/common";
import { useWintSalaryService } from "serviceHooks";
import WintSalaryStatusTags from "./wintSalaryStatusTags";
import ListMoreActionButton from "./moreActions";
import WintSalaryPaymentStatus from "./paymentStatus";
import useEarlyPayoutDialog from "../useEarlyPayoutDialog";
//#endregion
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import { DisplayType } from "models/enums/style";

const SalaryReportList: React.FC<{ reloadKey: number; reloadList: () => void }> = (props) => {
  const { reloadKey, reloadList } = props;
  const dispatch = useDispatch();
  const { getEarlyPayoutListDialog } = useEarlyPayoutDialog();
  const salaryService = useWintSalaryService();

  const { filter } = useFilterContext<SalaryFormTypes.WintSalaryReportFilter>();
  const searchContext = useSearchContext();

  const filterFunc = useCallback(
    (baseFilter: WintUtilsWebApi.Filter) =>
      salaryService
        .filterSalaryReport(baseFilter, {
          ...(filter ?? {}),
          personSearchName: searchContext?.searchText,
        })
        .then((res) => salaryService.convertReportToListItems(res)),
    [filter, searchContext?.searchText]
  );

  return (
    <TableAsync<WintSalaryReportListItem>
      filter={filterFunc}
      maxRowCount={10}
      includeSummary
      fetchSummaryWithData
      reloadKey={reloadKey}
      pagination="advanced"
      orderByProperty="yearMonth"
      orderByDescending={true}
      thenByProperty="personName"
      properties={{
        personName: {
          description: TranslationKeys.Common.Employee,
          formatter: (item: WintSuperkollAPIModelWintSalaryReport.WintSalaryReportListItem) =>
            new User(item.personId, item.personName),
          maxWidth: 150,
          noWrap: true,
        },
        yearMonth: {
          description: TranslationKeys.Common.Period,
          formatter: (item: WintSuperkollAPIModelWintSalaryReport.WintSalaryReportListItem) =>
            LocalizationHelperInstance.getFormattedDate(
              DateHelper.getDateFromYearMonth(item.yearMonth),
              DateConstants.MonthYearShortConfig
            ),
          noWrap: true,
          maxWidth: 80,
          classification: "date",
        },
        deviationAmount: {
          description: TranslationKeys.SalaryDeviation.Deviations,
          formatter: (item: WintSuperkollAPIModelWintSalaryReport.WintSalaryReportListItem) =>
            new Amount(CurrencyEnums.CurrencyCode.SEK, item.deviationAmount, "exceptZero"),
          alignment: "right",
          classification: "amount",
          summaryValueFormatter: (item: WintSuperkollAPIModelWintSalaryReport.WintSalaryReportListItem) =>
            new Amount(CurrencyEnums.CurrencyCode.SEK, item.deviationAmount, "exceptZero"),
        },
        socialFee: {
          description: TranslationKeys.Salary.SocialFees,
          formatter: (item: WintSuperkollAPIModelWintSalaryReport.WintSalaryReportListItem) =>
            new Amount(CurrencyEnums.CurrencyCode.SEK, item.socialFee),
          alignment: "right",
          classification: "amount",
          summaryValueFormatter: (item: WintSuperkollAPIModelWintSalaryReport.WintSalaryReportListItem) =>
            new Amount(CurrencyEnums.CurrencyCode.SEK, item.socialFee),
        },
        grossSalary: {
          description: TranslationKeys.Salary.GrossSalary,
          formatter: (item: WintSuperkollAPIModelWintSalaryReport.WintSalaryReportListItem) =>
            new Amount(CurrencyEnums.CurrencyCode.SEK, item.grossSalary),
          alignment: "right",
          classification: "amount",
          summaryValueFormatter: (item: WintSuperkollAPIModelWintSalaryReport.WintSalaryReportListItem) =>
            new Amount(CurrencyEnums.CurrencyCode.SEK, item.grossSalary),
        },
        netSalary: {
          description: TranslationKeys.Salary.NetSalary,
          formatter: (item: WintSuperkollAPIModelWintSalaryReport.WintSalaryReportListItem) => {
            if (item.hasEarlyPayouts) {
              return (
                <SimpleTooltip
                  text={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.ExtraPayoutListTooltip)}
                >
                  <span
                    className={cssExports.linkText}
                    style={{ display: "inline-flex", alignItems: "center" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch(
                        displayDialog(getEarlyPayoutListDialog(item.personId, item.yearMonth, item.personName ?? ""))
                      );
                    }}
                  >
                    {item.netSalary !== undefined
                      ? LocalizationHelperInstance.getFormattedCurrency(
                          item.netSalary,
                          CurrencyEnums.CurrencyCode.SEK,
                          0,
                          2
                        )
                      : ""}
                    <span style={{ marginLeft: 4, display: "inline-flex" }}>
                      <Icon icon={IconDefinitionConstants.WSplit} displayType={DisplayType.None} />
                    </span>
                  </span>
                </SimpleTooltip>
              );
            }
            return new Amount(CurrencyEnums.CurrencyCode.SEK, item.netSalary);
          },
          valueToPrint: (item: WintSuperkollAPIModelWintSalaryReport.WintSalaryReportListItem) =>
            LocalizationHelperInstance.getFormattedCurrency(item.netSalary ?? 0, CurrencyEnums.CurrencyCode.SEK, 0, 2),
          alignment: "right",
          classification: "amount",
          summaryValueFormatter: (item: WintSuperkollAPIModelWintSalaryReport.WintSalaryReportListItem) =>
            new Amount(CurrencyEnums.CurrencyCode.SEK, item.netSalary),
        },
        salaryState: {
          description: TranslationKeys.Common.Status,
          formatter: (item: WintSuperkollAPIModelWintSalaryReport.WintSalaryReportListItem) => {
            return item.salaryState !== undefined ? <WintSalaryStatusTags state={item.salaryState} /> : undefined;
          },
          noWrap: true,
          width: 80,
          disableSort: true,
        },
        paymentState: {
          description: TranslationKeys.Salary.WintSalaryPaymentStateColumnHeader,
          formatter: (item: WintSuperkollAPIModelWintSalaryReport.WintSalaryReportListItem) => {
            return item.paymentState !== undefined ? <WintSalaryPaymentStatus state={item.paymentState} /> : undefined;
          },
          alignment: "center",
          width: 50,
          disableSort: true,
        },
        moreActionColumn: {
          formatter: (item: WintSalaryReportListItem) => (
            <ListMoreActionButton
              personId={item.personId}
              yearMonth={item.yearMonth}
              personName={item.personName ?? ""}
              paymentState={item.paymentState}
              onActionSuccess={reloadList}
              payslipId={item.payslipId}
            />
          ),
          alignment: "left",
          disableSort: true,
          width: 30,
        },
      }}
    />
  );
};

export default SalaryReportList;
